import React, { useState, useEffect, useCallback } from "react";
import {
  FileText,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  RotateCcw,
  Ban,
  DollarSign,
  Hash,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { apiRequest } from "../lib/utils";
import { toast } from "./ToastProvider";

const statusLabels = {
  SCHEDULED: "Agendada",
  AUTHORIZED: "Autorizada",
  PROCESSING_CANCELLATION: "Cancelando",
  CANCELED: "Cancelada",
  CANCELLATION_DENIED: "Cancelamento negado",
  ERROR: "Erro",
  PENDING: "Pendente",
};

const statusColors = {
  SCHEDULED: "bg-blue-100 text-blue-800",
  AUTHORIZED: "bg-green-100 text-green-800",
  PROCESSING_CANCELLATION: "bg-yellow-100 text-yellow-800",
  CANCELED: "bg-slate-100 text-slate-800",
  CANCELLATION_DENIED: "bg-red-100 text-red-800",
  ERROR: "bg-red-100 text-red-800",
  PENDING: "bg-amber-100 text-amber-800",
};

export const NfseSection = () => {
  const [notas, setNotas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);

  const fetchNotas = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const [notasData, statsData] = await Promise.all([
        apiRequest(`/super-admin/nfse${params}`),
        apiRequest("/super-admin/nfse/stats"),
      ]);
      setNotas(notasData.notas || []);
      setStats(statsData);
    } catch (error) {
      console.error("Erro ao carregar NFS-e:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchNotas();
  }, [fetchNotas]);

  const handleReemitir = async (nfseId) => {
    if (!window.confirm("Deseja reemitir esta NFS-e?")) return;
    setActionLoading(nfseId);
    try {
      await apiRequest(`/super-admin/nfse/reemitir/${nfseId}`, { method: "POST" });
      toast.success("NFS-e reemitida com sucesso!");
      fetchNotas();
    } catch (error) {
      toast.error("Erro ao reemitir NFS-e");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelar = async (nfseId) => {
    if (!window.confirm("Tem certeza que deseja cancelar esta NFS-e?")) return;
    setActionLoading(nfseId);
    try {
      await apiRequest(`/super-admin/nfse/cancelar/${nfseId}`, { method: "POST" });
      toast.success("NFS-e cancelada com sucesso!");
      fetchNotas();
    } catch (error) {
      toast.error("Erro ao cancelar NFS-e");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("pt-BR");
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);
  };

  return (
    <div className="space-y-6 mt-8" data-testid="nfse-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            Notas Fiscais (NFS-e)
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Emissao automatica via Asaas a cada pagamento confirmado
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchNotas} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Total Emitidas</p>
                  <p className="text-2xl font-bold text-slate-800">{stats.emitidas}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Com Erro</p>
                  <p className="text-2xl font-bold text-red-600">{stats.erros}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Canceladas</p>
                  <p className="text-2xl font-bold text-slate-500">{stats.canceladas}</p>
                </div>
                <XCircle className="w-8 h-8 text-slate-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Valor Total</p>
                  <p className="text-xl font-bold text-teal-600">{formatCurrency(stats.total_valor)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-teal-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtro */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="AUTHORIZED">Autorizada</SelectItem>
            <SelectItem value="SCHEDULED">Agendada</SelectItem>
            <SelectItem value="ERROR">Com Erro</SelectItem>
            <SelectItem value="CANCELED">Cancelada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista de NFS-e */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </div>
      ) : notas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhuma NFS-e encontrada</p>
            <p className="text-xs text-slate-400 mt-1">
              As notas serao emitidas automaticamente a cada pagamento confirmado
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notas.map((nota) => (
            <Card key={nota.id} className="hover:shadow-md transition-shadow" data-testid={`nfse-card-${nota.id}`}>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800">
                        {nota.farmacia_nome || "Farmacia"}
                      </span>
                      <Badge className={statusColors[nota.status] || "bg-slate-100 text-slate-800"}>
                        {statusLabels[nota.status] || nota.status}
                      </Badge>
                      {nota.numero && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {nota.numero}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500 flex-wrap">
                      <span className="font-medium text-teal-700">{formatCurrency(nota.valor)}</span>
                      {nota.farmacia_cnpj && (
                        <span>CNPJ: {nota.farmacia_cnpj}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(nota.created_at)}
                      </span>
                    </div>
                    {nota.erro && (
                      <p className="text-xs text-red-500 mt-1">Erro: {nota.erro}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {nota.url_pdf && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={nota.url_pdf} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          PDF
                        </a>
                      </Button>
                    )}
                    {nota.status === "ERROR" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReemitir(nota.id)}
                        disabled={actionLoading === nota.id}
                      >
                        {actionLoading === nota.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Reemitir
                          </>
                        )}
                      </Button>
                    )}
                    {nota.status === "AUTHORIZED" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleCancelar(nota.id)}
                        disabled={actionLoading === nota.id}
                      >
                        {actionLoading === nota.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Ban className="w-3 h-3 mr-1" />
                            Cancelar
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NfseSection;
